<template>
  <div class="material-dashboard" v-loading="loading">
    <div class="page-header">
      <h2>자재 조달 현황</h2>
      <el-button size="small" @click="helpVisible = true">도움말</el-button>
      <HelpPanel v-model="helpVisible" :content-path="'screens/material-dashboard'" />
    </div>

    <!-- 요약 카드 -->
    <el-row :gutter="16" class="summary-row">
      <el-col :xs="12" :sm="4">
        <el-card shadow="hover" class="summary-card" :class="{ 'is-active': activeCard === 'not_ordered' }" @click="clickCard('not_ordered')">
          <div class="card-value text-danger">{{ summary.not_ordered }}</div>
          <div class="card-label">미발주</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="4">
        <el-card shadow="hover" class="summary-card" :class="{ 'is-active': activeCard === 'ordering' }" @click="clickCard('ordering')">
          <div class="card-value text-warning">{{ summary.ordering }}</div>
          <div class="card-label">발주중</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="4">
        <el-card shadow="hover" class="summary-card" :class="{ 'is-active': activeCard === 'receiving' }" @click="clickCard('receiving')">
          <div class="card-value text-primary">{{ summary.receiving }}</div>
          <div class="card-label">입고중</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="4">
        <el-card shadow="hover" class="summary-card" :class="{ 'is-active': activeCard === 'secured' }" @click="clickCard('secured')">
          <div class="card-value text-success">{{ summary.secured }}</div>
          <div class="card-label">확보완료</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="4">
        <el-card shadow="hover" class="summary-card card-danger" :class="{ 'is-active': activeCard === 'overdue' }" @click="clickCard('overdue')">
          <div class="card-value" :class="{ 'text-danger': summary.overdue > 0 }">
            {{ summary.overdue }}
          </div>
          <div class="card-label">지연</div>
        </el-card>
      </el-col>
      <el-col :xs="12" :sm="4">
        <el-card shadow="hover" class="summary-card" @click="clickCard(null)">
          <div class="card-value">{{ summary.total }}</div>
          <div class="card-label">전체</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 필터 바 -->
    <el-card class="filter-card">
      <el-form :inline="true" size="small">
        <el-form-item label="자재유형">
          <el-select v-model="filters.material_type" clearable placeholder="전체" style="width: 120px" @change="fetchData">
            <el-option label="원자재" value="RAW" />
            <el-option label="부자재" value="ACC" />
          </el-select>
        </el-form-item>
        <el-form-item label="조달상태">
          <el-select v-model="filters.procurement_status" clearable placeholder="전체" style="width: 140px" @change="fetchData">
            <el-option label="미발주" value="NOT_ORDERED" />
            <el-option label="일부발주" value="PARTIALLY_ORDERED" />
            <el-option label="발주완료" value="FULLY_ORDERED" />
            <el-option label="일부입고" value="PARTIALLY_RECEIVED" />
            <el-option label="입고완료" value="FULLY_RECEIVED" />
            <el-option label="공장전달완료" value="FACTORY_DELIVERED" />
          </el-select>
        </el-form-item>
        <el-form-item label="긴급도">
          <el-select v-model="filters.urgency" clearable placeholder="전체" style="width: 150px" @change="fetchData">
            <el-option label="지연 (마감초과)" value="overdue" />
            <el-option label="긴급 (7일이내)" value="urgent" />
            <!-- 자재 대사 Phase B (2026-08-02) — 다음 차수 후보 -->
            <el-option label="미출고 잔량 (입고됨·공장미전달)" value="unshipped" />
          </el-select>
        </el-form-item>
        <el-form-item label="브랜드">
          <el-select v-model="filters.brand" clearable filterable placeholder="전체" style="width: 150px" @change="fetchData">
            <el-option
              v-for="b in brands"
              :key="b.id"
              :label="b.name"
              :value="b.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetFilters">필터 초기화</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 데이터 테이블 -->
    <el-card class="table-card">
      <template #header>
        <div class="card-header">
          <span>자재 목록 ({{ items.length }}건)</span>
        </div>
      </template>

      <el-table
        :data="items"
        stripe
        highlight-current-row
        class="material-table"
        :row-class-name="tableRowClassName"
        empty-text="조건에 맞는 자재가 없습니다."
      >
        <el-table-column prop="request_number" label="지시번호" width="140" fixed>
          <template #default="{ row }">
            <el-link type="primary" @click.stop="goToMainRequest(row)">
              {{ row.request_number }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column prop="brand_name" label="브랜드" width="100" />
        <el-table-column label="자재유형" width="80" align="center">
          <template #default="{ row }">
            <el-tag :type="row.material_type === 'RAW' ? 'primary' : 'success'" size="small" effect="plain">
              {{ row.material_type_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="item_name" label="자재명" min-width="140" show-overflow-tooltip />
        <el-table-column prop="specification" label="규격" width="100" show-overflow-tooltip />
        <el-table-column prop="color" label="색상" width="80" />
        <el-table-column label="조달상태" width="110" align="center">
          <template #default="{ row }">
            <el-tag :type="procurementTagType(row.procurement_status)" size="small">
              {{ row.procurement_status_display }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="발주마감" width="130">
          <template #default="{ row }">
            <div>{{ row.order_deadline || '-' }}</div>
            <el-tag
              v-if="row.order_dday != null"
              :type="dDayTagType(row.order_dday)"
              size="small"
              effect="dark"
              class="dday-tag"
            >
              {{ formatDDay(row.order_dday) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="입고마감" width="130">
          <template #default="{ row }">
            <div>{{ row.receive_deadline || '-' }}</div>
            <el-tag
              v-if="row.receive_dday != null"
              :type="dDayTagType(row.receive_dday)"
              size="small"
              effect="plain"
              class="dday-tag"
            >
              {{ formatDDay(row.receive_dday) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="요척" width="75" align="right">
          <template #default="{ row }">
            <!-- 장당 소요량 (2026-07-12) — 필요량 = 요척 × 생산수량 -->
            {{ row.yardage != null ? Number(row.yardage) : '' }}
          </template>
        </el-table-column>
        <el-table-column label="필요량" width="90" align="right">
          <template #default="{ row }">
            {{ Number(row.required_quantity).toLocaleString() }}
          </template>
        </el-table-column>
        <el-table-column label="확보율" width="110">
          <template #default="{ row }">
            <el-progress
              :percentage="row.secured_percentage"
              :color="securedColor(row.secured_percentage)"
              :stroke-width="14"
              :text-inside="true"
            />
          </template>
        </el-table-column>
        <el-table-column prop="supplier_name" label="공급사" width="100" show-overflow-tooltip />
        <el-table-column label="액션" width="160" fixed="right" align="center">
          <template #default="{ row }">
            <el-button
              v-if="row.procurement_status === 'NOT_ORDERED' || row.procurement_status === 'PARTIALLY_ORDERED'"
              type="primary"
              size="small"
              link
              @click.stop="goToCreateOrder(row)"
            >
              발주생성
            </el-button>
            <el-button
              v-if="canReceive(row)"
              type="success"
              size="small"
              link
              @click.stop="openReceiving(row)"
            >
              입고
            </el-button>
            <el-button
              type="default"
              size="small"
              link
              @click.stop="goToMainRequest(row)"
            >
              상세
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 빠른 입고 다이얼로그 -->
    <QuickReceivingDialog ref="receivingDialogRef" @submitted="fetchData" />
  </div>
</template>

<script setup>
import HelpPanel from '@/components/help/HelpPanel.vue'
import { ref as _helpRef } from 'vue'
const helpVisible = _helpRef(false)
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axiosInstance from '@/api/axios'
import { useCodeStore } from '@/stores/codeStore'
import QuickReceivingDialog from '@/components/production/QuickReceivingDialog.vue'

const router = useRouter()
const codeStore = useCodeStore()
const loading = ref(false)
const receivingDialogRef = ref(null)

const summary = ref({
  not_ordered: 0,
  ordering: 0,
  receiving: 0,
  secured: 0,
  overdue: 0,
  total: 0,
})
const items = ref([])
const brands = computed(() => codeStore.brands)

const filters = reactive({
  material_type: '',
  procurement_status: '',
  urgency: '',
  brand: '',
})
const activeCard = ref(null)

// 요약 카드 클릭 → 필터 적용
const clickCard = (type) => {
  // 토글 동작
  if (activeCard.value === type) {
    activeCard.value = null
    filters.procurement_status = ''
    filters.urgency = ''
    fetchData()
    return
  }
  activeCard.value = type

  // 기존 상태/긴급도 필터 초기화
  filters.procurement_status = ''
  filters.urgency = ''

  if (type === 'not_ordered') {
    filters.procurement_status = 'NOT_ORDERED'
  } else if (type === 'ordering') {
    // 발주중 = PARTIALLY_ORDERED or FULLY_ORDERED → urgency 아님, 개별 status filter 사용 불가 (2개)
    // procurement_status는 단일값이므로 서버에서 별도 처리가 필요
    // 간단히 클라이언트에서 필터링
    filters.procurement_status = ''
  } else if (type === 'receiving') {
    filters.procurement_status = 'PARTIALLY_RECEIVED'
  } else if (type === 'secured') {
    // FULLY_RECEIVED or FACTORY_DELIVERED
    filters.procurement_status = ''
  } else if (type === 'overdue') {
    filters.urgency = 'overdue'
  }
  fetchData()
}

// 데이터 로드
const fetchData = async () => {
  loading.value = true
  try {
    const params = {}
    if (filters.material_type) params.material_type = filters.material_type
    if (filters.procurement_status) params.procurement_status = filters.procurement_status
    if (filters.urgency) params.urgency = filters.urgency
    if (filters.brand) params.brand = filters.brand

    const res = await axiosInstance.get('/production/main-requests/procurement-dashboard/', { params })
    summary.value = res.data.summary
    items.value = res.data.items

    // 카드별 추가 클라이언트 필터링 (서버가 단일 status만 지원)
    if (activeCard.value === 'ordering') {
      items.value = items.value.filter(i =>
        ['PARTIALLY_ORDERED', 'FULLY_ORDERED'].includes(i.procurement_status)
      )
    } else if (activeCard.value === 'secured') {
      items.value = items.value.filter(i =>
        ['FULLY_RECEIVED', 'FACTORY_DELIVERED'].includes(i.procurement_status)
      )
    }
  } catch (e) {
    console.error('자재 대시보드 로드 실패:', e)
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.material_type = ''
  filters.procurement_status = ''
  filters.urgency = ''
  filters.brand = ''
  activeCard.value = null
  fetchData()
}

// 네비게이션
const goToMainRequest = (row) => {
  router.push(`/main-requests/${row.main_request_id}`)
}

const goToCreateOrder = (row) => {
  const materialType = row.material_type === 'RAW' ? 'raw' : 'sub'
  router.push(`/material-orders/new?main_request=${row.main_request_id}&material_type=${materialType}`)
}

// 입고 가능 상태: 발주완료, 일부입고, 일부발주 (발주된 항목이 있는 경우)
const canReceive = (row) => {
  return ['PARTIALLY_ORDERED', 'FULLY_ORDERED', 'PARTIALLY_RECEIVED'].includes(row.procurement_status)
}

const openReceiving = (row) => {
  receivingDialogRef.value?.open(row)
}

// === 포맷팅 헬퍼 ===
const formatDDay = (days) => {
  if (days == null) return '-'
  if (days === 0) return 'D-Day'
  return days > 0 ? `D-${days}` : `D+${Math.abs(days)}`
}

const dDayTagType = (days) => {
  if (days == null) return 'info'
  if (days < 0) return 'danger'
  if (days <= 3) return 'danger'
  if (days <= 7) return 'warning'
  return 'info'
}

const procurementTagType = (status) => {
  const map = {
    NOT_ORDERED: 'danger',
    PARTIALLY_ORDERED: 'warning',
    FULLY_ORDERED: 'primary',
    PARTIALLY_RECEIVED: 'warning',
    FULLY_RECEIVED: 'success',
    FACTORY_DELIVERED: 'success',
  }
  return map[status] || 'info'
}

const securedColor = (rate) => {
  if (rate >= 100) return '#67C23A'
  if (rate >= 60) return '#409EFF'
  if (rate >= 30) return '#E6A23C'
  return '#F56C6C'
}

const tableRowClassName = ({ row }) => {
  if (row.is_overdue) return 'row-overdue'
  if (row.is_urgent) return 'row-urgent'
  return ''
}

onMounted(async () => {
  await codeStore.fetchBrands()
  await fetchData()
})
</script>

<style scoped>
.material-dashboard {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 22px;
}

/* 요약 카드 */
.summary-row {
  margin-bottom: 16px;
}

.summary-card {
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 12px;
}

.summary-card:hover {
  transform: translateY(-2px);
}

.summary-card.is-active {
  border-color: #409EFF;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.3);
}

.card-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

.card-label {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
}

.text-danger { color: #F56C6C; }
.text-warning { color: #E6A23C; }
.text-primary { color: #409EFF; }
.text-success { color: #67C23A; }
.text-info { color: #909399; }

/* 필터 카드 */
.filter-card {
  margin-bottom: 16px;
}

.filter-card :deep(.el-card__body) {
  padding: 12px 16px;
}

.filter-card .el-form-item {
  margin-bottom: 0;
  margin-right: 16px;
}

/* 테이블 */
.table-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.material-table {
  width: 100%;
}

.dday-tag {
  margin-top: 2px;
}

/* 행 스타일 */
:deep(.row-overdue) {
  background-color: #FEF0F0 !important;
}

:deep(.row-urgent) {
  background-color: #FDF6EC !important;
}
</style>
